import { useState, useEffect } from 'react'
import { Table, Card, Tag, Button, Space, Modal, message } from 'antd'
import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import api from '../../services/api'

const statusMap: Record<number, { label: string; color: string }> = {
  0: { label: '待审核', color: 'orange' },
  1: { label: '已开票', color: 'green' },
  2: { label: '已拒绝', color: 'red' }
}

const typeMap: Record<number, string> = { 0: '普通发票', 1: '增值税专用发票' }

export default function InvoiceApplications() {
  const [loading, setLoading] = useState(false)
  const [list, setList] = useState<any[]>([])
  const [detail, setDetail] = useState<any>(null)
  const [statusFilter, setStatusFilter] = useState<number | undefined>(undefined)

  const loadList = async () => {
    setLoading(true)
    try {
      const params: any = {}
      if (statusFilter !== undefined) params.status = statusFilter
      const res: any = await api.get('/invoice-applications', { params })
      setList(res.data?.list || [])
    } catch { message.error('加载失败') }
    finally { setLoading(false) }
  }

  useEffect(() => { loadList() }, [statusFilter])

  const handleReview = async (id: number, approve: boolean) => {
    Modal.confirm({
      title: approve ? '确认通过' : '确认拒绝',
      content: approve ? '标记此开票申请为已开票？' : '拒绝此开票申请？',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const url = approve
            ? `/invoice-applications/${id}/invoiced`
            : `/invoice-applications/${id}/reject`;
          const res: any = await api.put(url, {})
          if (res.code === 0) {
            message.success(approve ? '已标记为已开票' : '已拒绝')
            loadList()
          } else {
            message.error(res.message || '操作失败')
          }
        } catch { message.error('操作失败') }
      }
    })
  }

  const columns = [
    { title: '企业名称', dataIndex: 'title', key: 'title', ellipsis: true },
    { title: '发票类型', dataIndex: 'invoice_type', key: 'invoice_type', width: 120,
      render: (v: number) => typeMap[v] || v },
    { title: '税号', dataIndex: 'tax_no', key: 'tax_no', width: 180, ellipsis: true },
    { title: '开票金额', dataIndex: 'amount', key: 'amount', width: 120,
      render: (v: number) => `¥${Number(v).toFixed(2)}` },
    { title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (v: number) => {
        const s = statusMap[v] || { label: '未知', color: 'default' }
        return <Tag color={s.color}>{s.label}</Tag>
      }
    },
    { title: '申请时间', dataIndex: 'created_at', key: 'created_at', width: 170,
      render: (v: string) => v ? new Date(v).toLocaleString() : '-' },
    {
      title: '操作', key: 'action', width: 150,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => setDetail(record)}>详情</Button>
          {record.status === 0 && (
            <>
              <Button size="small" type="primary" icon={<CheckOutlined />}
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                onClick={() => handleReview(record.id, true)}>通过</Button>
              <Button size="small" danger icon={<CloseOutlined />}
                onClick={() => handleReview(record.id, false)}>拒绝</Button>
            </>
          )}
        </Space>
      )
    }
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>开票申请审核</h2>
        <Space>
          <span style={{ color: '#999', fontSize: 13 }}>企业端提交的开票申请</span>
          <span style={{ cursor: 'pointer', color: '#1890ff' }} onClick={loadList}>刷新</span>
        </Space>
      </div>

      <Card>
        <div style={{ marginBottom: 12 }}>
          <Space>
            {[undefined, 0, 1, 2].map(s => (
              <Button key={String(s)} type={statusFilter === s ? 'primary' : 'default'} size="small"
                onClick={() => setStatusFilter(s)}>
                {s === undefined ? '全部' : statusMap[s]?.label}
              </Button>
            ))}
          </Space>
        </div>
        <Table dataSource={list} columns={columns} rowKey="id" loading={loading}
          pagination={{ pageSize: 10, showTotal: (t: number) => `共 ${t} 条` }} />
      </Card>

      {/* 详情弹窗 */}
      <Modal title="开票申请详情" open={!!detail} onCancel={() => setDetail(null)} footer={null} width={520}>
        {detail && (
          <div style={{ lineHeight: 2.4 }}>
            <p><strong>发票抬头：</strong>{detail.title}</p>
            <p><strong>发票类型：</strong>{typeMap[detail.invoice_type] || detail.invoice_type}</p>
            <p><strong>税号：</strong>{detail.tax_no || '未填写'}</p>
            <p><strong>开票金额：</strong><span style={{ color: '#ff4d4f', fontWeight: 700, fontSize: 16 }}>¥{Number(detail.amount).toFixed(2)}</span></p>
            <p><strong>对账单ID：</strong>{detail.statement_id}</p>
            <p><strong>申请人类型：</strong>{detail.applicant_type === 2 ? '企业端' : '管理端'}</p>
            <p><strong>申请人ID：</strong>{detail.applicant_id}</p>
            <p><strong>状态：</strong><Tag color={statusMap[detail.status]?.color}>{statusMap[detail.status]?.label}</Tag></p>
            {detail.remark && <p><strong>备注：</strong>{detail.remark}</p>}
            <p><strong>申请时间：</strong>{detail.created_at ? new Date(detail.created_at).toLocaleString() : '-'}</p>
            {detail.reviewed_at && <p><strong>处理时间：</strong>{new Date(detail.reviewed_at).toLocaleString()}</p>}
          </div>
        )}
      </Modal>
    </div>
  )
}
